import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CreditCard, Clock, CheckCircle2, XCircle, TrendingDown, TrendingUp } from 'lucide-react';
import { format } from 'date-fns';
import { formatCurrency } from '@/lib/types/job';

interface AdjustmentTransaction {
  id: string;
  adjustment_id: string;
  total_amount_cents: number;
  platform_fee_cents: number;
  vendor_payout_cents: number;
  cc_processing_fee_cents?: number;
  status: string;
  payment_method_type: string;
  payment_method_last4?: string;
  payment_captured_at?: string;
  ach_settlement_expected_at?: string;
  ach_settled_at?: string;
  created_at: string;
  po_modification_requests?: {
    reason?: string;
  } | null;
}

interface AdjustmentPaymentsListProps {
  purchaseOrderId: string;
  transactions: AdjustmentTransaction[];
  showVendorFinancials?: boolean;
}

export function AdjustmentPaymentsList({ transactions, showVendorFinancials = false }: AdjustmentPaymentsListProps) {
  if (!transactions || transactions.length === 0) {
    return null;
  }

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'transfer_complete':
        return {
          label: 'Funds Released',
          icon: TrendingUp,
          className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
        };
      case 'held_in_escrow':
      case 'payment_captured':
        return {
          label: 'Held in Escrow',
          icon: CheckCircle2,
          className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
        };
      case 'ach_pending':
        return {
          label: 'ACH Pending',
          icon: Clock,
          className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
        };
      case 'failed':
        return {
          label: 'Failed',
          icon: XCircle,
          className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
        };
      default:
        return {
          label: 'Processing',
          icon: Clock,
          className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
        };
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Change Order Payments
        </CardTitle>
        <CardDescription>Payment transactions for PO change orders</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {transactions.map((tx) => {
            const statusConfig = getStatusConfig(tx.status);
            const StatusIcon = statusConfig.icon;
            const isCredit = tx.total_amount_cents < 0;
            const ccFeeCents = tx.cc_processing_fee_cents ?? 0;
            const baseAmount = tx.total_amount_cents;

            return (
              <div key={tx.id} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    {isCredit ? (
                      <TrendingDown className="h-5 w-5 text-green-600 dark:text-green-400" />
                    ) : (
                      <TrendingUp className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                    )}
                    <div>
                      <p className="font-medium">
                        {isCredit ? 'Credit' : 'Additional Charge'} - {formatCurrency(tx.total_amount_cents)}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Adjustment Payment
                      </p>
                    </div>
                  </div>
                  <Badge className={`${statusConfig.className} flex items-center gap-1.5`}>
                    <StatusIcon className="h-3 w-3" />
                    {statusConfig.label}
                  </Badge>
                </div>

                {/* Payment Breakdown */}
                {!isCredit && (
                  <div className="bg-muted/50 rounded-md p-3 space-y-2">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">Change Order Amount</span>
                      <span>{formatCurrency(baseAmount)}</span>
                    </div>
                    {ccFeeCents > 0 && (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-muted-foreground flex items-center gap-1.5">
                          <CreditCard className="h-3 w-3" />
                          CC Processing Fee
                        </span>
                        <span className="text-orange-600">{formatCurrency(ccFeeCents)}</span>
                      </div>
                    )}
                    <div className="border-t pt-2 flex justify-between items-center text-sm font-medium">
                      <span>Total Charged</span>
                      <span>{formatCurrency(baseAmount + ccFeeCents)}</span>
                    </div>
                  </div>
                )}

                <div className={`grid ${showVendorFinancials ? 'grid-cols-2' : 'grid-cols-1'} gap-4 text-sm`}>
                  <div>
                    <p className="text-muted-foreground">Payment Method</p>
                    <div className="flex items-center gap-1 font-medium">
                      <CreditCard className="h-3 w-3" />
                      {tx.payment_method_type === 'us_bank_account' ? 'Bank Account' : 'Card'}
                      {tx.payment_method_last4 && ` •••• ${tx.payment_method_last4}`}
                    </div>
                  </div>
                  {showVendorFinancials && (
                    <div>
                      <p className="text-muted-foreground">Platform Fee</p>
                      <p className="font-medium">{formatCurrency(tx.platform_fee_cents)}</p>
                    </div>
                  )}
                </div>

                {tx.ach_settlement_expected_at && tx.status === 'ach_pending' && (
                  <div className="text-sm">
                    <p className="text-muted-foreground">Expected Settlement</p>
                    <p className="font-medium">
                      {format(new Date(tx.ach_settlement_expected_at), 'PPP')}
                    </p>
                  </div>
                )}

                {tx.payment_captured_at && (
                  <div className="text-sm">
                    <p className="text-muted-foreground">Captured</p>
                    <p className="font-medium">
                      {format(new Date(tx.payment_captured_at), 'PPP')}
                    </p>
                  </div>
                )}

                {tx.ach_settled_at && (
                  <div className="text-sm">
                    <p className="text-muted-foreground">Settled</p>
                    <p className="font-medium">
                      {format(new Date(tx.ach_settled_at), 'PPP')}
                    </p>
                  </div>
                )}

                <div className="text-xs text-muted-foreground pt-2 border-t">
                  Processed on {format(new Date(tx.created_at), 'PPP p')}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
